// src/pages/employer/EmployerDashboard.jsx

import React, { useEffect, useState } from "react";
import {
  Briefcase,
  PlusCircle,
  Users,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function EmployerDashboard() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");
  const name = localStorage.getItem("name") || "Employer";

  useEffect(() => {
    fetchEmployerJobs();
  }, []);

  const fetchEmployerJobs = async () => {
    try {
      const response = await fetch(
        "https://digital-worker-exchange.onrender.com/api/jobs/employer",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setJobs(data);
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error("Failed to load jobs", error);
    } finally {
      setLoading(false);
    }
  };

  const totalJobs = jobs.length;

  const activeJobs = jobs.filter(
    (job) => job.status === "open"
  ).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 pt-28 px-6 pb-20">

      <div className="max-w-7xl mx-auto">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">

          <div>
            <h1 className="text-4xl font-extrabold text-slate-900">
              Welcome, {name}
            </h1>

            <p className="text-slate-600 mt-2">
              Manage your jobs and connect with skilled workers.
            </p>
          </div>

          <Link
            to="/postjob"
            className="mt-4 md:mt-0 flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-orange-500 text-white font-semibold shadow-lg hover:shadow-xl"
          >
            <PlusCircle size={20} />
            Post New Job
          </Link>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">

          <div className="bg-white rounded-3xl shadow-lg p-6 border">
            <Briefcase
              className="text-purple-600"
              size={30}
            />

            <h3 className="text-3xl font-bold mt-4">
              {totalJobs}
            </h3>

            <p className="text-slate-500">
              Total Jobs Posted
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-lg p-6 border">
            <Users
              className="text-emerald-600"
              size={30}
            />

            <h3 className="text-3xl font-bold mt-4">
              {activeJobs}
            </h3>

            <p className="text-slate-500">
              Active Jobs
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-lg p-6 border">
            <Users
              className="text-orange-500"
              size={30}
            />

            <h3 className="text-3xl font-bold mt-4">
              {jobs.reduce(
                (acc, job) =>
                  acc + (job.applicants?.length || 0),
                0
              )}
            </h3>

            <p className="text-slate-500">
              Total Applicants
            </p>
          </div>

        </div>

        {/* RECENT JOBS */}
        <div className="mt-12">

          <div className="flex items-center justify-between mb-5">
            <h2 className="text-2xl font-bold">
              Your Jobs
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2
                className="animate-spin"
                size={32}
              />
            </div>
          ) : jobs.length === 0 ? (
            <div className="bg-white rounded-3xl border shadow p-12 text-center">

              <h3 className="text-xl font-semibold">
                No Jobs Posted Yet
              </h3>

              <p className="text-slate-500 mt-2">
                Create your first job and start
                receiving applications.
              </p>

              <Link
                to="/postjob"
                className="inline-block mt-6 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-orange-500 text-white"
              >
                Post Job
              </Link>

            </div>
          ) : (
            <div className="space-y-4">

              {jobs.map((job) => (
                <Link
                  key={job._id}
                  to={`/employer/job/${job._id}`}
                  className="block bg-white border rounded-2xl shadow-sm hover:shadow-md transition p-5"
                >
                  <div className="flex justify-between items-center">

                    <div>
                      <h3 className="text-lg font-bold text-slate-900">
                        {job.title}
                      </h3>

                      <p className="text-sm text-slate-500 mt-1">
                        {job.trade} • {job.location}
                      </p>

                      <p className="text-sm text-emerald-600 mt-2">
                        ₹{job.minPay} - ₹{job.maxPay}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-xs ${
                          job.status === "open"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {job.status}
                      </span>

                      <ArrowRight
                        size={20}
                        className="text-slate-500"
                      />
                    </div>

                  </div>
                </Link>
              ))}

            </div>
          )}

        </div>

      </div>
    </div>
  );
}